/**
 * Database Transform Utilities
 *
 * Bidirectional transformations between database schema and application layer.
 * Clean separation of database concerns from business logic.
 *
 * @module dbTransforms
 */

/**
 * Transform database snake_case to application camelCase
 */
export function dbToApp<T extends Record<string, any>>(dbRecord: T): any {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(dbRecord)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    // Recursively transform nested objects
    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      result[camelKey] = dbToApp(value);
    }
    // Transform arrays of objects
    else if (Array.isArray(value)) {
      result[camelKey] = value.map(item =>
        (item && typeof item === 'object' && !(item instanceof Date)) ? dbToApp(item) : item
      );
    }
    // Keep primitive values and dates as-is
    else {
      result[camelKey] = value;
    }
  }

  return result;
}

/**
 * Transform application camelCase to database snake_case
 */
export function appToDb<T extends Record<string, any>>(appRecord: T): any {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(appRecord)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();

    // Recursively transform nested objects
    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      result[snakeKey] = appToDb(value);
    }
    // Transform arrays of objects
    else if (Array.isArray(value)) {
      result[snakeKey] = value.map(item =>
        (item && typeof item === 'object' && !(item instanceof Date)) ? appToDb(item) : item
      );
    }
    // Keep primitive values and dates as-is
    else {
      result[snakeKey] = value;
    }
  }

  return result;
}

/**
 * Transform database boolean (0/1 or true/false) to application boolean
 */
export function dbBoolToApp(value: number | boolean | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return value;
  return value === 1;
}

/**
 * Transform application boolean to database number (0/1)
 */
export function appBoolToDb(value: boolean | null | undefined): number {
  if (value === null || value === undefined) return 0;
  return value ? 1 : 0;
}

/**
 * Transform database timestamp (string) to Date object
 */
export function dbTimestampToApp(timestamp: string | null | undefined): Date | null {
  if (!timestamp) return null;
  return new Date(timestamp);
}

/**
 * Transform Date object to database timestamp (ISO string)
 */
export function appDateToDbTimestamp(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  if (typeof date === 'string') return date;
  return date.toISOString();
}

/**
 * Transform database DECIMAL/NUMERIC (string) to application number
 */
export function dbDecimalToApp(decimal: string | number | null | undefined): number {
  if (decimal === null || decimal === undefined) return 0;
  if (typeof decimal === 'number') return decimal;
  const parsed = parseFloat(decimal);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Transform application number to database DECIMAL (number, DB will handle precision)
 */
export function appNumberToDbDecimal(value: number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  return value;
}

/**
 * Transform database JSON column (string or object) to application object
 */
export function dbJsonToApp<T = any>(json: string | T | null | undefined): T | null {
  if (!json) return null;
  if (typeof json === 'string') {
    try {
      return JSON.parse(json) as T;
    } catch {
      return null;
    }
  }
  return json as T;
}

/**
 * Transform application object to database JSON (string)
 */
export function appObjectToDbJson<T = any>(obj: T | null | undefined): string | null {
  if (!obj) return null;
  if (typeof obj === 'string') return obj;
  try {
    return JSON.stringify(obj);
  } catch {
    return null;
  }
}

/**
 * Normalize database enum to uppercase
 */
export function dbEnumToApp(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.toUpperCase();
}

/**
 * Normalize application enum to uppercase for database
 */
export function appEnumToDb(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.toUpperCase();
}

/**
 * Transform database ID (can be string or number) to application number
 */
export function dbIdToApp(id: string | number | null | undefined): number | null {
  if (id === null || id === undefined) return null;
  if (typeof id === 'number') return id;
  const parsed = parseInt(id, 10);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Transform application ID (number) to database (number, DB handles conversion)
 */
export function appIdToDb(id: number | string | null | undefined): number | null {
  if (id === null || id === undefined) return null;
  if (typeof id === 'number') return id;
  const parsed = parseInt(id, 10);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Batch transform array of database records to application format
 */
export function dbArrayToApp<T extends Record<string, any>>(records: T[]): any[] {
  return records.map(record => dbToApp(record));
}

/**
 * Batch transform array of application records to database format
 */
export function appArrayToDb<T extends Record<string, any>>(records: T[]): any[] {
  return records.map(record => appToDb(record));
}

/**
 * Transform database transaction record to application format
 * Specialized transformer for transactions with all field conversions
 */
export function dbTransactionToApp(dbTransaction: any): any {
  const transformed = {
    ...dbToApp(dbTransaction),
    amount: dbDecimalToApp(dbTransaction.amount),
    isPaid: dbBoolToApp(dbTransaction.is_paid),
    status: dbEnumToApp(dbTransaction.status) || 'PENDING', // Handle status field with default
    isScheduled: dbBoolToApp(dbTransaction.is_scheduled),
    isRecurring: dbBoolToApp(dbTransaction.is_recurring),
    createdAt: dbTimestampToApp(dbTransaction.created_at),
    updatedAt: dbTimestampToApp(dbTransaction.updated_at),
    date: dbTransaction.date, // Keep as-is (already date string)
    dueDate: dbTransaction.due_date,
    competenceDate: dbTransaction.competence_date,
  };

  // Sync isPaid with status for backward compatibility
  if (transformed.status === 'PAID') {
    transformed.isPaid = true;
  } else if (transformed.isPaid && transformed.status === 'PENDING') {
    transformed.status = 'PAID';
  }

  return transformed;
}

/**
 * Transform application transaction to database format
 * Specialized transformer for transactions with all field conversions
 */
export function appTransactionToDb(appTransaction: any): any {
  // Sync status with isPaid for consistency
  let finalStatus = appTransaction.status || 'PENDING';
  let finalIsPaid = appTransaction.isPaid;

  if (appTransaction.isPaid && appTransaction.status !== 'PAID') {
    finalStatus = 'PAID';
  } else if (!appTransaction.isPaid && appTransaction.status === 'PAID') {
    finalIsPaid = true;
  }

  return {
    ...appToDb(appTransaction),
    amount: appNumberToDbDecimal(appTransaction.amount),
    is_paid: appBoolToDb(finalIsPaid),
    is_scheduled: appBoolToDb(appTransaction.isScheduled),
    is_recurring: appBoolToDb(appTransaction.isRecurring),
    type: appEnumToDb(appTransaction.type),
    status: appEnumToDb(finalStatus),
  };
}
